/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * MARKETING CONSTANTS — Single source of truth for all landing page data.
 * Change it here, it updates everywhere. No more find-and-replace across 40 files.
 *
 * Rules:
 * - Tool count comes from src/lib/ai/tools/registry.ts (count the Map entries)
 * - Pricing comes from this file (not hardcoded in components)
 * - CTA text, trust indicators, and trial messaging are centralized here
 * - If you need to update the tool count, update AI_TOOL_COUNT below
 */

import { EMAILS } from "@/lib/constants/master";

// ---------------------------------------------------------------------------
// Product Facts (update when the product changes)
// ---------------------------------------------------------------------------

/** Number of registered AI tools in the Hank tool registry */
export const AI_TOOL_COUNT = 47;

/** Number of trades with pre-loaded cost data */
export const TRADES_COUNT = 8;

/** Free trial duration in days */
export const FREE_TRIAL_DAYS = 7;

/** Starting monthly price (Starter plan) */
export const STARTING_PRICE = 79;

// ---------------------------------------------------------------------------
// CTA Text (used across all marketing pages)
// ---------------------------------------------------------------------------

export const CTA = {
  /** Primary CTA button text */
  PRIMARY: "Start Your Free Trial",
  /** Primary CTA href */
  PRIMARY_HREF: "/auth/sign-up",
  /** Secondary CTA options */
  SECONDARY_DEMO: "Book a Demo",
  SECONDARY_CONTACT: "Contact Us",
  SECONDARY_HOW: "See How It Works",
  /** Contact email */
  CONTACT_HREF: `mailto:${EMAILS.CONTACT}`,
} as const;

// ---------------------------------------------------------------------------
// Trust Indicators (used in CTA sections and hero areas)
// ---------------------------------------------------------------------------

export const TRUST = {
  /** Standard trust strip (3 items) */
  STRIP: [
    `${FREE_TRIAL_DAYS}-day free trial`,
    "No long-term contracts",
    "Cancel anytime",
  ] as const,
  /** Extended trust strip (4 items) */
  STRIP_EXTENDED: [
    `${FREE_TRIAL_DAYS}-day free trial`,
    "No credit card required",
    "No long-term contracts",
    "Cancel anytime",
  ] as const,
  /** Inline trust string (for CTA sections) */
  INLINE: `${FREE_TRIAL_DAYS}-day free trial • No credit card required • Cancel anytime`,
} as const;

// ---------------------------------------------------------------------------
// Brand Copy (used in headers, footers, meta)
// ---------------------------------------------------------------------------

export const BRAND = {
  /** Full product name */
  NAME: "Contractor Co-Pilot",
  /** Short name (for body copy after first mention) */
  SHORT: "CC",
  /** Tagline */
  TAGLINE:
    "The business platform for contractors. Simple. Connected. Pre-loaded for your trade.",
  /** Meta description */
  META_DESCRIPTION:
    "The business platform for contractors. Estimates, proposals, projects, budgets, invoices, and an AI assistant built for the trades.",
  /** Launch state */
  LAUNCH_STATE: "Early Access" as const,
} as const;

// ---------------------------------------------------------------------------
// Feature Claims (safe to use on marketing pages — verified against codebase)
// ---------------------------------------------------------------------------

export const CLAIMS = {
  /** AI assistant description */
  AI_DESCRIPTION: `${AI_TOOL_COUNT} tools, plain English, your data`,
  /** Pre-loaded data */
  PRELOADED: `${TRADES_COUNT} trades pre-loaded with costs`,
  /** Trial messaging */
  TRIAL: `${FREE_TRIAL_DAYS}-day free trial on every plan`,
  /** Starting price */
  PRICE: `Starting at $${STARTING_PRICE}/mo`,
} as const;
